import React from 'react';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DesignSystem from '../designSystem';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { useMarketingPageMeta } from '../marketing/useMarketingPageMeta';

export default function PresentationPage() {
  const navigate = useNavigate();
  useThemeMode();
  useMarketingPageMeta(
    'VisiMe — Local services platform',
    'Discover and connect with local service providers in Switzerland. VisiMe for web, iOS, and Android.',
    '/presentation',
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: DesignSystem.colors.canvas,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Solid fill first so text contrast is real even when gradient support differs.
          backgroundColor: DesignSystem.colors.action,
          backgroundImage: `linear-gradient(135deg, ${DesignSystem.colors.action} 0%, ${DesignSystem.colors.actionDark} 100%)`,
          color: DesignSystem.colors.onPrimary,
          padding: DesignSystem.spacing.xxxl,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontWeight: DesignSystem.typography.displayLarge.fontWeight,
              fontSize: DesignSystem.typography.displayLarge.fontSize,
              lineHeight: DesignSystem.typography.displayLarge.lineHeight,
              mb: DesignSystem.spacing.lg,
            }}
          >
            VisiMe
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: DesignSystem.typography.h2.fontWeight,
              fontSize: DesignSystem.typography.h2.fontSize,
              mb: DesignSystem.spacing.xl,
              opacity: 0.95,
            }}
          >
            Your Local Services Platform
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: DesignSystem.typography.bodyLarge.fontSize,
              lineHeight: DesignSystem.typography.bodyLarge.lineHeight,
              mb: DesignSystem.spacing.xxxl,
              opacity: 0.9,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Connect with local service providers in your area. From photography to event planning, 
            find the perfect professional for your needs.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/?login=1')}
            sx={{
              // White fill + action ink stays ≥4.5:1 in both modes (primary is too light in dark).
              backgroundColor: DesignSystem.colors.onPrimary,
              color: DesignSystem.colors.action,
              boxShadow: DesignSystem.shadows.md,
              padding: `${DesignSystem.spacing.lg} ${DesignSystem.spacing.xxl}`,
              borderRadius: DesignSystem.borderRadius.xl,
              fontWeight: DesignSystem.typography.buttonLarge.fontWeight,
              fontSize: DesignSystem.typography.buttonLarge.fontSize,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: DesignSystem.colors.onPrimary,
                filter: 'brightness(0.96)',
                transform: 'translateY(-2px)',
              },
              transition: `all ${DesignSystem.animation.normal} ${DesignSystem.animation.easing}`,
            }}
          >
            Get Started
          </Button>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          padding: `${DesignSystem.spacing.xxxl} 0`,
          backgroundColor: DesignSystem.colors.surface,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: DesignSystem.typography.h1.fontWeight,
              color: DesignSystem.colors.textPrimary,
              mb: DesignSystem.spacing.xxxl,
            }}
          >
            Why Choose VisiMe?
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: DesignSystem.spacing.xl,
            }}
          >
            {[
              {
                title: 'Local Experts',
                description: 'Find verified professionals in your area',
              },
              {
                title: 'Easy Booking',
                description: 'Connect and book services directly',
              },
              {
                title: 'Trusted Reviews',
                description: 'See ratings and reviews from real customers',
              },
            ].map((feature, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  padding: DesignSystem.spacing.xl,
                  borderRadius: DesignSystem.borderRadius.md,
                  border: `1px solid ${DesignSystem.colors.border}`,
                  backgroundColor: DesignSystem.colors.surface,
                  color: DesignSystem.colors.textPrimary,
                  textAlign: 'center',
                  transition: `all ${DesignSystem.animation.normal} ${DesignSystem.animation.easing}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: DesignSystem.colors.primary,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: DesignSystem.typography.h3.fontWeight,
                    color: DesignSystem.colors.textPrimary,
                    mb: DesignSystem.spacing.md,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: DesignSystem.colors.textSecondary,
                    fontSize: DesignSystem.typography.body.fontSize,
                  }}
                >
                  {feature.description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          padding: `${DesignSystem.spacing.xxxl} 0`,
          backgroundColor: DesignSystem.colors.surfaceElevated,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            sx={{
              fontWeight: DesignSystem.typography.h1.fontWeight,
              color: DesignSystem.colors.textPrimary,
              mb: DesignSystem.spacing.lg,
            }}
          >
            Ready to Get Started?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: DesignSystem.colors.textSecondary,
              fontSize: DesignSystem.typography.bodyLarge.fontSize,
              mb: DesignSystem.spacing.xl,
            }}
          >
            Sign in to browse local services and connect with professionals
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/?login=1')}
            sx={{
              backgroundColor: DesignSystem.colors.action,
              color: DesignSystem.colors.onPrimary,
              padding: `${DesignSystem.spacing.lg} ${DesignSystem.spacing.xxl}`,
              borderRadius: DesignSystem.borderRadius.xl,
              fontWeight: DesignSystem.typography.buttonLarge.fontWeight,
              fontSize: DesignSystem.typography.buttonLarge.fontSize,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: DesignSystem.colors.actionDark,
                transform: 'translateY(-2px)',
              },
              transition: `all ${DesignSystem.animation.normal} ${DesignSystem.animation.easing}`,
            }}
          >
            Sign In
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

